import { useState } from 'react'

import { Tooltip } from 'react-tooltip'

import ChainToken from '@src/components/ChainToken'
import CopyToClipboard from '@src/components/CopyToClipboard'
import InfoIcon from '@src/components/icons/Info'
import { NATIVE_TOKEN_ADDRESS } from '@src/configs/chainInfo'
import { CCTP_TOOLTIP, MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { LiquiditySource, StepNames } from '@src/enums'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import TransactionSteps from '@src/pages/Exchange/components/SwapInProgressFlow/TransactionSteps'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { isFeatureEnabled } from '@src/utils/environment'
import { formatExchangeAmountWithCommas } from '@src/utils/exchangeUtils'
import { formatExchangePrice } from '@src/utils/formatExchangePrice'
import { formatNumber } from '@src/utils/formatFee'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useConfigStore } from '@src/zustand_stores'

interface ITransactionDetailsProps {
    sellData: IExchangeData
    buyData: IExchangeData
    fees: IFee[]
    destinationAddress?: string
    transactionHash?: string
    currentStep?: StepNames
    liquiditySource: LiquiditySource
    isCCTPTransfer: boolean
}
export default function TransactionDetails({
    sellData,
    buyData,
    fees,
    destinationAddress,
    transactionHash,
    currentStep = StepNames.UNSTARTED,
    liquiditySource,
    isCCTPTransfer,
}: ITransactionDetailsProps) {
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const { evmWalletAddress } = useWalletConnections()
    const [showWrappedBalanceTooltip, setShowWrappedBalanceTooltip] = useState(false)
    const [showTestnetFeesTooltip, setShowTestnetFeesTooltip] = useState(false)
    const isTestnet = isFeatureEnabled('USE_TESTNETS_ONLY')

    const { wrappedNativeBalance } = useExchangeBalances()

    const isNativeToken = sellData.token?.address === NATIVE_TOKEN_ADDRESS[sellData.chainId]
    const wrappedSymbol = isNativeToken ? `W${sellData.token?.symbol}` : ''

    return (
        <>
            {/* You Pay Section */}
            <div className='space-y-2'>
                <p className='text-gray-500'>You Pay</p>
                <div className='flex items-center justify-between rounded-xl bg-[#1A1B1E] p-4 text-base font-medium'>
                    <div className='flex items-center gap-2'>
                        <img
                            src={sellData.token?.logoURI}
                            className='h-[20px] w-[20px] rounded-full'
                            onError={e => {
                                e.currentTarget.src = '/assets/token/unknown.png'
                            }}
                        />
                        <span className='max-w-[180px] truncate'>
                            {formatExchangeAmountWithCommas(sellData)} {sellData.token?.symbol}
                        </span>
                    </div>
                    <div className='flex items-center gap-2'>
                        <img
                            src={availableNetworks[sellData.chainId]?.icon}
                            className='h-[20px] w-[20px] rounded-full'
                        />
                        <span className='max-w-[130px] truncate'>{availableNetworks[sellData.chainId]?.name}</span>
                    </div>
                </div>

                {isNativeToken && (
                    <div className='mt-2 flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                        <div className='flex items-center gap-2'>
                            <span className='text-mach-sub-text'>Current {wrappedSymbol} Balance</span>
                        </div>
                        <div className='flex items-center gap-1 text-sm'>
                            <div className='flex items-center gap-[6px]'>
                                <ChainToken
                                    tokenLogo={`/assets/token/w${sellData.token?.symbol?.toLowerCase()}.png`}
                                    tokenIconSize={14}
                                    chainIconSize={12}
                                />
                                <span className='max-w-[180px] truncate'>
                                    {wrappedNativeBalance &&
                                        wrappedNativeBalance.formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)}
                                </span>
                            </div>
                            <span>on</span>
                            <div className='flex items-center gap-[6px]'>
                                <img
                                    src={availableNetworks[sellData.chainId]?.icon}
                                    className='h-[14px] w-[14px] rounded-full'
                                />
                                <span>{availableNetworks[sellData.chainId]?.name}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Progress Steps Section */}
            {!currentStep.includes('MATCH') && (
                <TransactionSteps
                    currentStep={currentStep}
                    sellData={sellData}
                    buyData={buyData}
                    liquiditySource={liquiditySource}
                />
            )}

            {/* You Receive Section */}
            <div className='space-y-2'>
                <p className='text-gray-500'>You Receive</p>
                <div className='flex items-center justify-between rounded-xl bg-[#1A1B1E] p-4 text-base font-medium'>
                    <div className='flex items-center gap-2'>
                        <img
                            src={buyData.token?.logoURI}
                            className='h-[20px] w-[20px] rounded-full'
                            onError={e => {
                                e.currentTarget.src = '/assets/token/unknown.png'
                            }}
                        />
                        <span className='max-w-[180px] truncate'>
                            {formatExchangeAmountWithCommas(buyData)} {buyData.token?.symbol}
                        </span>
                    </div>
                    <div className='flex items-center gap-2'>
                        <img
                            src={availableNetworks[buyData.chainId]?.icon}
                            className='h-[20px] w-[20px] rounded-full'
                        />
                        <span>{availableNetworks[buyData.chainId]?.name}</span>
                    </div>
                </div>
            </div>

            <div className='space-y-4 rounded-xl bg-[#1A1B1E]/50 p-4'>
                {/* Recipient */}
                <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                    <span className='text-mach-sub-text'>Recipient Address</span>
                    <div className='flex items-center gap-[6px]'>
                        <CopyToClipboard
                            anchorId='dest-address-2'
                            textToCopy={destinationAddress || evmWalletAddress!}
                        />
                        <span className='text-sm'>{truncateAddress(destinationAddress || evmWalletAddress!)}</span>
                    </div>
                </div>

                {/* CCTP Information */}
                {isCCTPTransfer && (
                    <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                        <span className='text-mach-sub-text'>Transfer Type</span>
                        <div className='flex items-center gap-2'>
                            <span
                                className='w-fit cursor-help items-center rounded-md bg-blue-500/20 px-2 py-0.5 text-xs text-blue-400'
                                data-tooltip-id='cctp-transfer-info'
                            >
                                CCTP Transfer
                            </span>
                            <Tooltip
                                id='cctp-transfer-info'
                                style={{ zIndex: 1000, maxWidth: '300px' }}
                                content={CCTP_TOOLTIP}
                            />
                            <span className='text-xs text-mach-sub-text'>(5-20 min confirmation time)</span>
                        </div>
                    </div>
                )}

                {/* Total Fees */}
                <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                    <div className='flex items-center gap-2'>
                        <span className='text-mach-sub-text'>Total Fees</span>
                        {isTestnet && (
                            <div className='relative'>
                                <div
                                    className='cursor-pointer'
                                    onMouseEnter={() => setShowTestnetFeesTooltip(true)}
                                    onMouseLeave={() => setShowTestnetFeesTooltip(false)}
                                >
                                    <InfoIcon className='h-4 w-4 text-mach-sub-text' />
                                </div>
                                {showTestnetFeesTooltip && (
                                    <div className='absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-md bg-mach-modal-background p-3 text-xs text-white shadow-lg'>
                                        Gas fees are difficult to estimate on testnet. Only network gas fees will be
                                        charged.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {liquiditySource === LiquiditySource.cctpDirect ||
                    fees.reduce((res, cur) => (res += cur.value), 0) === 0 ? (
                        <span className='w-fit items-center rounded-md bg-mach-mach-green px-2 py-0.5 text-xs text-white'>
                            FREE
                        </span>
                    ) : (
                        <span className='text-sm'>
                            ${formatNumber(fees.reduce((res, cur) => (res += cur.value), 0))}
                        </span>
                    )}
                </div>

                {/* Rate */}
                <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                    <span className='text-mach-sub-text'>Rate</span>
                    <span className='flex items-center text-sm'>
                        1&nbsp;
                        <span className='max-w-20 truncate'>{sellData.token?.symbol}</span>
                        &nbsp;=&nbsp;
                        {formatExchangePrice(sellData, buyData)}&nbsp;
                        <span className='max-w-20 truncate'>{buyData.token?.symbol}</span>
                    </span>
                </div>

                {/* Transaction Hash */}
                {transactionHash && (
                    <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                        <div className='flex items-center gap-1'>
                            <span className='text-mach-sub-text'>Transaction Hash</span>
                        </div>
                        <div className='flex items-center gap-[6px]'>
                            <span className='text-sm'>{truncateAddress(transactionHash)}</span>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}
